import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Divider from '@mui/joy/Divider';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import AgentLoadDialog from '../components/agent/AgentLoadDialog';

const AgentFileActionsContext = createContext(null);

export function AgentFileProvider({ children }) {
  const [buildApi, setBuildApi] = useState(null);
  const buildApiRef = useRef(null);
  const [fileSession, setFileSession] = useState(null);
  const fileSessionRef = useRef(null);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [agentFileMenuOpen, setAgentFileMenuOpen] = useState(false);

  useEffect(() => {
    buildApiRef.current = buildApi;
  }, [buildApi]);

  useEffect(() => {
    fileSessionRef.current = fileSession;
  }, [fileSession]);

  const register = useCallback((api) => {
    setBuildApi(api);
    return () => setBuildApi(null);
  }, []);

  /** Called from Home to sync active tab + setter for Observe/Numbering load behaviour. Pass null on unmount. */
  const syncFileSession = useCallback((next) => {
    setFileSession(next);
  }, []);

  const handleLoadSelect = useCallback((id) => {
    setLoadDialogOpen(false);
    const fs = fileSessionRef.current;
    if (!fs) return;
    if (fs.activeTab === 0) {
      buildApiRef.current?.loadAgentById?.(id);
    } else {
      fs.setSelectedAgentId?.(id);
    }
  }, []);

  const activeTab = fileSession?.activeTab ?? 0;

  const openAgentFileMenu = useCallback(() => setAgentFileMenuOpen(true), []);

  /** Close current agent: clear selected id everywhere; reset editor if available. */
  const closeCurrentAgent = useCallback(() => {
    setAgentFileMenuOpen(false);
    setLoadDialogOpen(false);
    const fs = fileSessionRef.current;
    fs?.setSelectedAgentId?.(null);
    buildApiRef.current?.closeAgent?.();
  }, []);

  const value = useMemo(
    () => ({
      register,
      syncFileSession,
      openLoadDialog: () => setLoadDialogOpen(true),
      loadDialogOpen,
      setLoadDialogOpen,
      handleLoadSelect,
      buildApi,
      activeTab,
      fileSession,
      agentFileMenuOpen,
      setAgentFileMenuOpen,
      openAgentFileMenu,
      closeCurrentAgent,
    }),
    [register, syncFileSession, loadDialogOpen, handleLoadSelect, buildApi, activeTab, fileSession, agentFileMenuOpen, openAgentFileMenu, closeCurrentAgent]
  );

  return (
    <AgentFileActionsContext.Provider value={value}>
      {children}
      <AgentLoadDialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        onSelect={handleLoadSelect}
      />
    </AgentFileActionsContext.Provider>
  );
}

export function useAgentFileActions() {
  const ctx = useContext(AgentFileActionsContext);
  if (!ctx) {
    throw new Error('useAgentFileActions must be used within AgentFileProvider');
  }
  return ctx;
}

/** Agent menu (load/save/…) for the fixed header; button label "Agent" with folder icon. */
export function AgentFileMenu() {
  const { openLoadDialog, buildApi, activeTab, agentFileMenuOpen, setAgentFileMenuOpen, closeCurrentAgent } = useAgentFileActions();
  const onBuild = activeTab === 0;
  const canFileOps = onBuild && buildApi;

  return (
    <Dropdown
      open={agentFileMenuOpen}
      onOpenChange={(_event, open) => setAgentFileMenuOpen(open)}
    >
      <MenuButton
        variant="outlined"
        color="neutral"
        size="sm"
        aria-haspopup="menu"
        startDecorator={<FolderIcon />}
      >
        Agent
      </MenuButton>
      <Menu size="sm" variant="soft" sx={{ minWidth: 200 }}>
        <MenuItem onClick={() => openLoadDialog()} startDecorator={<FolderOpenIcon />}>
          Load…
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!canFileOps || !buildApi?.hasAgentName || !buildApi?.canSave}
          onClick={() => buildApi?.saveAgent?.()}
          startDecorator={<CheckCircleOutlineIcon />}
        >
          Save
        </MenuItem>
        <MenuItem
          disabled={!canFileOps || !buildApi?.hasAgentName}
          onClick={() => buildApi?.saveDuplicateAgent?.()}
          startDecorator={<ContentCopyIcon />}
        >
          Save as (duplicate)
        </MenuItem>
        <MenuItem
          disabled={!canFileOps || !buildApi?.canDownload}
          onClick={() => buildApi?.download?.()}
          startDecorator={<FileDownloadIcon />}
        >
          Download
        </MenuItem>
        <MenuItem
          disabled={!canFileOps}
          onClick={() => buildApi?.upload?.()}
          startDecorator={<FileUploadIcon />}
        >
          Upload…
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => closeCurrentAgent()} startDecorator={<CloseIcon />}>
          Close
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}
